import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { User, HolidayHome, Booking, BragPost, BookingRequest, Review, Invitation, AccessRequest, ContentPage, ContentPageType } from '../types';
import * as usersService from '../services/rtdb/users.service';
import * as homesService from '../services/rtdb/homes.service';
import * as bookingsService from '../services/rtdb/bookings.service';
import * as bragsService from '../services/rtdb/brags.service';
import * as reviewsService from '../services/rtdb/reviews.service';
import * as invitationsService from '../services/rtdb/invitations.service';
import * as accessRequestsService from '../services/rtdb/accessRequests.service';
import * as contentPagesService from '../services/rtdb/contentPages.service';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import * as settingsService from '../services/rtdb/settings.service';
import { getWeeklyRate } from '../utils/seasonUtils';

interface AppState {
  // State
  users: User[];
  homes: HolidayHome[];
  bookings: Booking[];
  bragPosts: BragPost[];
  reviews: Review[];
  invitations: Invitation[];
  accessRequests: AccessRequest[];
  contentPages: ContentPage[];
  familyBookingInfo: string;
  currentUser: User | null;
  loading: boolean;
  error: string | null;

  // Firestore listeners
  unsubscribeAll: () => void;
  initializeListeners: () => void;

  // Actions
  setCurrentUser: (user: User | null) => void;

  // User actions
  addUser: (user: Omit<User, 'id'>) => Promise<void>;
  updateUser: (id: string, updates: Partial<User>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;

  // Home actions
  addHome: (home: Omit<HolidayHome, 'id'>) => Promise<void>;
  updateHome: (id: string, updates: Partial<HolidayHome>) => Promise<void>;
  deleteHome: (id: string) => Promise<void>;

  // Booking actions
  checkAvailability: (homeId: string, startDate: Date, endDate: Date, excludeBookingId?: string) => Promise<{ available: boolean; conflictingBookings: Booking[]; blockedReason?: string }>;
  createBooking: (booking: BookingRequest) => Promise<{ success: boolean; error?: string; referenceNumber?: string }>;
  editBooking: (id: string, updates: { startDate: Date; endDate: Date }) => Promise<{ success: boolean; error?: string }>;
  updateBooking: (id: string, updates: Partial<Booking>) => Promise<void>;
  cancelBooking: (id: string) => Promise<void>;
  deleteBooking: (id: string) => Promise<void>;

  // Brag actions
  addBragPost: (post: Omit<BragPost, 'id' | 'timestamp'>) => Promise<void>;
  deleteBragPost: (postId: string) => Promise<void>;
  reactToBrag: (postId: string, userId: string, reaction: 'like' | 'dislike') => Promise<void>;
  commentOnBrag: (postId: string, userId: string, text: string) => Promise<void>;
  deleteBragComment: (postId: string, commentId: string) => Promise<void>;

  // Review actions
  addReview: (review: Omit<Review, 'id' | 'createdAt'>) => Promise<{ success: boolean; error?: string }>;
  deleteReview: (reviewId: string) => Promise<void>;
  getReviewsForHome: (homeId: string) => Review[];
  getReviewForBooking: (bookingId: string) => Review | undefined;
  getAverageRating: (homeId: string) => number;

  // Invitation actions
  createInvitation: (email: string, role: 'family' | 'familyMember' | 'guest') => Promise<{ success: boolean; error?: string; token?: string }>;
  acceptInvitation: (token: string, userData: { id: string; name: string; email: string }) => Promise<{ success: boolean; error?: string }>;
  cancelInvitation: (invitationId: string) => Promise<void>;
  getInvitationByToken: (token: string) => Promise<Invitation | null>;

  // Access request actions
  submitAccessRequest: (email: string, message?: string) => Promise<{ success: boolean; error?: string }>;
  approveAccessRequest: (id: string, role: 'family' | 'familyMember' | 'guest', allowedHomeIds?: string[]) => Promise<void>;
  deleteAccessRequest: (id: string) => Promise<void>;
  rejectAccessRequest: (id: string) => Promise<void>;

  // Content page actions
  getContentPage: (type: ContentPageType) => ContentPage | undefined;
  updateContentPage: (pageId: string, updates: Partial<ContentPage>) => Promise<void>;
  initializeContentPages: () => Promise<void>;

  // Settings actions
  saveFamilyBookingInfo: (text: string) => Promise<void>;

  // Utility functions
  getBookingsForHome: (homeId: string) => Booking[];
  getBookingsForUser: (userId: string) => Booking[];
  getUserById: (id: string) => User | undefined;
  getHomeById: (id: string) => HolidayHome | undefined;
}

// Keep track of unsubscribe functions
let unsubscribeFunctions: (() => void)[] = [];

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // Initial state
      users: [],
      homes: [],
      bookings: [],
      bragPosts: [],
      reviews: [],
      invitations: [],
      accessRequests: [],
      contentPages: [],
      familyBookingInfo: '',
      currentUser: null,
      loading: false,
      error: null,

      // Initialize Firestore listeners
      initializeListeners: () => {
        // Unsubscribe from any existing listeners first
        get().unsubscribeAll();

        const onError = (name: string) => (error: Error) => {
          console.error(`Error listening to ${name}:`, error);
        };

        // Subscribe to users
        const unsubUsers = usersService.subscribeToUsers((users) => {
          set({ users });
        }, onError('users'));

        // Subscribe to homes
        const unsubHomes = homesService.subscribeToHomes((homes) => {
          set({ homes });
        }, onError('homes'));

        // Subscribe to bookings
        const unsubBookings = bookingsService.subscribeToBookings((bookings) => {
          set({ bookings });
        }, onError('bookings'));

        // Subscribe to brags
        const unsubBrags = bragsService.subscribeToBrags((bragPosts) => {
          set({ bragPosts });
        }, onError('brags'));

        // Subscribe to reviews
        const unsubReviews = reviewsService.subscribeToReviews((reviews) => {
          set({ reviews });
        }, onError('reviews'));

        // Subscribe to invitations
        const unsubInvitations = invitationsService.subscribeToInvitations((invitations) => {
          set({ invitations });
        }, onError('invitations'));

        // Subscribe to content pages
        const unsubContentPages = contentPagesService.subscribeToContentPages((contentPages) => {
          set({ contentPages });
        }, onError('contentPages'));

        // Subscribe to access requests
        const unsubAccessRequests = accessRequestsService.subscribeToAccessRequests((accessRequests) => {
          set({ accessRequests });
        }, onError('accessRequests'));

        // Subscribe to settings
        const unsubSettings = settingsService.subscribeToFamilyBookingInfo((familyBookingInfo) => {
          set({ familyBookingInfo });
        }, onError('settings'));

        // Store unsubscribe functions
        unsubscribeFunctions = [unsubUsers, unsubHomes, unsubBookings, unsubBrags, unsubReviews, unsubInvitations, unsubContentPages, unsubAccessRequests, unsubSettings];
      },

      // Unsubscribe from all listeners
      unsubscribeAll: () => {
        unsubscribeFunctions.forEach((unsub) => unsub());
        unsubscribeFunctions = [];
      },

      // Actions
      setCurrentUser: (user) => set({ currentUser: user }),
      
      // User actions
      addUser: async (userData) => {
        try {
          const id = `pending-${Date.now()}`;
          const user: User = { ...userData, id };
          await usersService.saveUser(user);
        } catch (error) {
          console.error('Error adding user:', error);
          set({ error: 'Failed to add user' });
        }
      },

      updateUser: async (id, updates) => {
        try {
          await usersService.updateUser(id, updates);
        } catch (error) {
          console.error('Error updating user:', error);
          set({ error: 'Failed to update user' });
        }
      },

      deleteUser: async (id) => {
        try {
          const user = get().users.find(u => u.id === id);
          const deleteFn = httpsCallable(functions, 'deleteUserCompletely');
          await deleteFn({ userId: id, email: user?.email });
        } catch (error) {
          console.error('Error deleting user:', error);
          set({ error: 'Failed to delete user' });
        }
      },
      
      // Home actions
      addHome: async (homeData) => {
        try {
          const id = Date.now().toString();
          const home: HolidayHome = { ...homeData, id };
          await homesService.saveHome(home);
        } catch (error) {
          console.error('Error adding home:', error);
          set({ error: 'Failed to add home' });
        }
      },

      updateHome: async (id, updates) => {
        try {
          await homesService.updateHome(id, updates);
        } catch (error) {
          console.error('Error updating home:', error);
          set({ error: 'Failed to update home' });
        }
      },

      deleteHome: async (id) => {
        try {
          await homesService.deleteHome(id);
        } catch (error) {
          console.error('Error deleting home:', error);
          set({ error: 'Failed to delete home' });
        }
      },
      
      // Booking actions
      checkAvailability: async (homeId, startDate, endDate, excludeBookingId) => {
        try {
          const home = get().homes.find(h => h.id === homeId);

          // Check for blocked dates on the home
          if (home?.availability && home.availability.length > 0) {
            const blockedResult = bookingsService.checkBlockedDates(
              home.availability,
              startDate,
              endDate
            );
            if (blockedResult.blocked) {
              return {
                available: false,
                conflictingBookings: [],
                blockedReason: blockedResult.reason || 'Property is not available during these dates',
              };
            }
          }

          // Check for conflicting bookings
          const result = await bookingsService.checkAvailability(homeId, startDate, endDate, excludeBookingId);
          return {
            available: result.available,
            conflictingBookings: result.conflictingBookings,
          };
        } catch (error) {
          console.error('Error checking availability:', error);
          return { available: false, conflictingBookings: [] };
        }
      },

      createBooking: async (bookingData) => {
        try {
          const home = get().homes.find(h => h.id === bookingData.homeId);
          const user = get().currentUser;

          // Check availability before creating booking
          const availability = await get().checkAvailability(
            bookingData.homeId,
            new Date(bookingData.startDate),
            new Date(bookingData.endDate)
          );

          if (!availability.available) {
            if (availability.blockedReason) {
              return { success: false, error: availability.blockedReason };
            }
            return {
              success: false,
              error: 'These dates are not available. There is already a booking for this period.',
            };
          }

          const id = Date.now().toString();
          const year = new Date(bookingData.startDate).getFullYear();
          const referenceNumber = `${year}-${id.slice(-6)}`;

          let listPrice = 0;
          if (home) {
            const startDate = new Date(bookingData.startDate);
            const endDate = new Date(bookingData.endDate);
            const nights = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

            if (home.pricingStructure.seasonalConfig) {
              listPrice = Math.round(getWeeklyRate(startDate, home.pricingStructure.seasonalConfig) * nights);
            } else {
              const baseRate = home.pricingStructure.userTypeOverrides?.guest || home.pricingStructure.weekdayRate;
              listPrice = baseRate * nights;
            }
          }

          // Also check users array as fallback (in case currentUser hasn't fully synced)
          const userFromList = get().users.find(u => u.id === bookingData.userId);
          const resolvedUser = user ?? userFromList;
          const isOwner = Boolean(resolvedUser?.isFamily || resolvedUser?.isAdmin);
          const isFamilyMember = !isOwner && Boolean(resolvedUser?.isFamilyMember);
          const nights = home
            ? Math.ceil((new Date(bookingData.endDate).getTime() - new Date(bookingData.startDate).getTime()) / (1000 * 60 * 60 * 24))
            : 0;
          const guests = bookingData.guests ?? 1;

          let totalPrice: number;
          if (isOwner) {
            totalPrice = 0;
          } else if (isFamilyMember) {
            totalPrice = 20 * nights * guests;
          } else {
            totalPrice = listPrice;
          }

          const cleaningFee = home?.structuredContent?.paymentTerms?.cleaningFee ?? 0;
          if ((isOwner || isFamilyMember) && bookingData.cleaningOption === 'service') {
            totalPrice += cleaningFee;
          } else if (!isOwner && !isFamilyMember) {
            totalPrice += cleaningFee;
          }

          const booking: Booking = {
            ...bookingData,
            id,
            referenceNumber,
            status: 'pending',
            totalPrice,
            listPrice: listPrice !== totalPrice ? listPrice : undefined,
            guests: (isOwner || isFamilyMember) ? guests : undefined,
            isPaid: false,
            cleaningOption: (isOwner || isFamilyMember) ? bookingData.cleaningOption : undefined,
          };
          await bookingsService.saveBooking(booking);
          return { success: true, referenceNumber };
        } catch (error) {
          console.error('Error creating booking:', error);
          set({ error: 'Failed to create booking' });
          return { success: false, error: 'Failed to create booking' };
        }
      },

      editBooking: async (id, updates) => {
        try {
          const booking = get().bookings.find(b => b.id === id);
          if (!booking) {
            return { success: false, error: 'Booking not found' };
          }

          if (booking.status !== 'pending') {
            return { success: false, error: 'Only pending bookings can be edited' };
          }

          const home = get().homes.find(h => h.id === booking.homeId);
          const user = get().users.find(u => u.id === booking.userId);

          // Check availability for new dates, excluding current booking
          const availability = await get().checkAvailability(
            booking.homeId,
            updates.startDate,
            updates.endDate,
            id
          );

          if (!availability.available) {
            if (availability.blockedReason) {
              return { success: false, error: availability.blockedReason };
            }
            return {
              success: false,
              error: 'These dates are not available. There is already a booking for this period.',
            };
          }

          // Recalculate price using same role logic as createBooking
          const isOwner = (user?.isFamily || user?.isAdmin) ?? false;
          const isFamilyMember = (!isOwner && (user?.isFamilyMember ?? false));
          const nights = Math.ceil(
            (updates.endDate.getTime() - updates.startDate.getTime()) / (1000 * 60 * 60 * 24)
          );

          let listPrice = 0;
          if (home) {
            if (home.pricingStructure.seasonalConfig) {
              listPrice = Math.round(getWeeklyRate(updates.startDate, home.pricingStructure.seasonalConfig) * nights);
            } else {
              const baseRate = home.pricingStructure.userTypeOverrides?.guest || home.pricingStructure.weekdayRate;
              listPrice = baseRate * nights;
            }
          }

          let totalPrice: number;
          if (isOwner) {
            totalPrice = 0;
          } else if (isFamilyMember) {
            totalPrice = 20 * nights * (booking.guests ?? 1);
          } else {
            totalPrice = listPrice + (home?.structuredContent?.paymentTerms?.cleaningFee ?? 0);
          }

          await bookingsService.updateBooking(id, {
            startDate: updates.startDate,
            endDate: updates.endDate,
            totalPrice,
            listPrice: listPrice !== totalPrice ? listPrice : undefined,
          });

          return { success: true };
        } catch (error) {
          console.error('Error editing booking:', error);
          return { success: false, error: 'Failed to edit booking' };
        }
      },

      updateBooking: async (id, updates) => {
        try {
          await bookingsService.updateBooking(id, updates);
        } catch (error) {
          console.error('Error updating booking:', error);
          set({ error: 'Failed to update booking' });
        }
      },

      cancelBooking: async (id) => {
        try {
          await bookingsService.updateBooking(id, { status: 'cancelled' });
        } catch (error) {
          console.error('Error cancelling booking:', error);
          set({ error: 'Failed to cancel booking' });
        }
      },

      deleteBooking: async (id) => {
        try {
          await bookingsService.deleteBooking(id);
        } catch (error) {
          console.error('Error deleting booking:', error);
          set({ error: 'Failed to delete booking' });
        }
      },
      
      // Brag actions
      addBragPost: async (postData) => {
        try {
          const id = Date.now().toString();
          const post: BragPost = {
            ...postData,
            id,
            timestamp: new Date(),
            reactions: {},
          };
          await bragsService.saveBrag(post);
        } catch (error) {
          console.error('Error adding brag post:', error);
          set({ error: 'Failed to add brag post' });
        }
      },

      deleteBragPost: async (postId) => {
        try {
          await bragsService.deleteBrag(postId);
        } catch (error) {
          console.error('Error deleting brag post:', error);
          set({ error: 'Failed to delete brag post' });
        }
      },

      reactToBrag: async (postId, userId, reaction) => {
        try {
          await bragsService.toggleReaction(postId, userId, reaction);
        } catch (error) {
          console.error('Error reacting to brag:', error);
          set({ error: 'Failed to react to brag' });
        }
      },

      commentOnBrag: async (postId, userId, text) => {
        try {
          await bragsService.addComment(postId, userId, text);
        } catch (error) {
          console.error('Error commenting on brag:', error);
          set({ error: 'Failed to comment on brag' });
        }
      },

      deleteBragComment: async (postId, commentId) => {
        try {
          await bragsService.deleteComment(postId, commentId);
        } catch (error) {
          console.error('Error deleting brag comment:', error);
          set({ error: 'Failed to delete brag comment' });
        }
      },

      // Review actions
      addReview: async (reviewData) => {
        try {
          // Check if a review already exists for this booking
          const existingReview = get().reviews.find(r => r.bookingId === reviewData.bookingId);
          if (existingReview) {
            return { success: false, error: 'A review already exists for this booking' };
          }

          const id = Date.now().toString();
          const review: Review = {
            ...reviewData,
            id,
            createdAt: new Date(),
          };
          await reviewsService.saveReview(review);
          return { success: true };
        } catch (error) {
          console.error('Error adding review:', error);
          return { success: false, error: 'Failed to add review' };
        }
      },

      deleteReview: async (reviewId) => {
        try {
          await reviewsService.deleteReview(reviewId);
        } catch (error) {
          console.error('Error deleting review:', error);
          set({ error: 'Failed to delete review' });
        }
      },

      getReviewsForHome: (homeId) => {
        return get().reviews.filter(review => review.homeId === homeId);
      },

      getReviewForBooking: (bookingId) => {
        return get().reviews.find(review => review.bookingId === bookingId);
      },

      getAverageRating: (homeId) => {
        const homeReviews = get().reviews.filter(review => review.homeId === homeId);
        return reviewsService.calculateAverageRating(homeReviews);
      },

      // Invitation actions
      createInvitation: async (email, role) => {
        try {
          const currentUser = get().currentUser;
          if (!currentUser) {
            return { success: false, error: 'You must be logged in to send invitations' };
          }

          // Check if user can invite this role
          if (role === 'family' && !currentUser.isAdmin) {
            return { success: false, error: 'Only admins can invite family members' };
          }

          // Check if email is already in the system
          const existingUser = get().users.find(u => u.email.toLowerCase() === email.toLowerCase());
          if (existingUser) {
            return { success: false, error: 'This email is already registered' };
          }

          // Check if there's already a pending invitation
          const existingInvitation = get().invitations.find(
            i => i.email.toLowerCase() === email.toLowerCase() && i.status === 'pending'
          );
          if (existingInvitation) {
            return { success: false, error: 'An invitation has already been sent to this email' };
          }

          const id = Date.now().toString();
          const token = invitationsService.generateInviteToken();
          const expiresAt = new Date();
          expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiry

          const invitation: Invitation = {
            id,
            email: email.toLowerCase(),
            invitedBy: currentUser.id,
            role,
            status: 'pending',
            token,
            createdAt: new Date(),
            expiresAt,
          };

          await invitationsService.saveInvitation(invitation);
          return { success: true, token };
        } catch (error) {
          console.error('Error creating invitation:', error);
          return { success: false, error: 'Failed to create invitation' };
        }
      },

      acceptInvitation: async (token, userData) => {
        try {
          const invitation = await invitationsService.getInvitationByToken(token);
          if (!invitation) {
            return { success: false, error: 'Invalid invitation' };
          }

          if (invitation.status !== 'pending') {
            return { success: false, error: 'This invitation has already been used' };
          }

          if (invitationsService.isInvitationExpired(invitation)) {
            await invitationsService.updateInvitation(invitation.id, { status: 'expired' });
            return { success: false, error: 'This invitation has expired' };
          }

          if (invitation.email.toLowerCase() !== userData.email.toLowerCase()) {
            return { success: false, error: 'This invitation is for a different email address' };
          }

          // Create the user
          const user: User = {
            id: userData.id,
            name: userData.name,
            email: userData.email.toLowerCase(),
            isAdmin: false,
            isFamily: invitation.role === 'family',
            isFamilyMember: invitation.role === 'familyMember',
            referredBy: invitation.invitedBy,
            status: 'approved',
          };

          await usersService.saveUser(user);

          // Mark invitation as accepted
          await invitationsService.updateInvitation(invitation.id, { status: 'accepted' });

          return { success: true };
        } catch (error) {
          console.error('Error accepting invitation:', error);
          return { success: false, error: 'Failed to accept invitation' };
        }
      },

      cancelInvitation: async (invitationId) => {
        try {
          await invitationsService.deleteInvitation(invitationId);
        } catch (error) {
          console.error('Error cancelling invitation:', error);
          set({ error: 'Failed to cancel invitation' });
        }
      },

      getInvitationByToken: async (token) => {
        return await invitationsService.getInvitationByToken(token);
      },

      // Access request actions
      submitAccessRequest: async (email, message) => {
        try {
          // Check if request already exists
          const existing = get().accessRequests.find(
            r => r.email.toLowerCase() === email.toLowerCase() && r.status === 'pending'
          );
          if (existing) {
            return { success: false, error: 'Für diese E-Mail-Adresse wurde bereits eine Anfrage gestellt.' };
          }
          // Check if already a user
          const existingUser = get().users.find(u => u.email.toLowerCase() === email.toLowerCase());
          if (existingUser) {
            return { success: false, error: 'Diese E-Mail-Adresse hat bereits Zugang zur Plattform.' };
          }
          await accessRequestsService.createAccessRequest(email, message);
          return { success: true };
        } catch (error) {
          console.error('Error submitting access request:', error);
          return { success: false, error: 'Anfrage konnte nicht gesendet werden.' };
        }
      },

      approveAccessRequest: async (id, role, allowedHomeIds) => {
        try {
          await accessRequestsService.updateAccessRequest(id, {
            status: 'approved',
            approvedRole: role,
            approvedHomeIds: allowedHomeIds?.length ? allowedHomeIds : undefined,
            approvedAt: new Date(),
          });
          // Cloud Function handles magic link + Resend email automatically
        } catch (error) {
          console.error('Error approving access request:', error);
        }
      },

      rejectAccessRequest: async (id) => {
        try {
          await accessRequestsService.updateAccessRequest(id, { status: 'rejected' });
        } catch (error) {
          console.error('Error rejecting access request:', error);
        }
      },

      deleteAccessRequest: async (id) => {
        try {
          await accessRequestsService.deleteAccessRequest(id);
        } catch (error) {
          console.error('Error deleting access request:', error);
        }
      },

      // Content page actions
      getContentPage: (type) => {
        return get().contentPages.find(page => page.type === type && page.isPublished);
      },

      updateContentPage: async (pageId, updates) => {
        try {
          await contentPagesService.updateContentPage(pageId, {
            ...updates,
            lastUpdatedAt: new Date(),
            lastUpdatedBy: get().currentUser?.id || '',
          });
        } catch (error) {
          console.error('Error updating content page:', error);
          set({ error: 'Failed to update content page' });
        }
      },

      initializeContentPages: async () => {
        const currentUser = get().currentUser;
        if (currentUser?.isAdmin) {
          await contentPagesService.initializeDefaultContentPages(currentUser.id);
        }
      },

      saveFamilyBookingInfo: async (text) => {
        await settingsService.saveFamilyBookingInfo(text);
        set({ familyBookingInfo: text });
      },

      // Utility functions
      getBookingsForHome: (homeId) => {
        return get().bookings.filter(booking => booking.homeId === homeId);
      },
      
      getBookingsForUser: (userId) => {
        return get().bookings.filter(booking => booking.userId === userId);
      },
      
      getUserById: (id) => {
        return get().users.find(user => user.id === id);
      },
      
      getHomeById: (id) => {
        return get().homes.find(home => home.id === id);
      },
    }),
    {
      name: 'family-holiday-store',
    }
  )
);